// src/utils/crossAppUrlResolver.ts

/**
 * Cross-App URL Resolver
 *
 * Resolves tokenized URLs (e.g., "cms://path") to full URLs using environment variables.
 * This allows cross-app navigation configs to work across different environments.
 *
 * Environment variable format:
 * VITE_CROSS_APP_{TOKEN}_URL=<base_url>
 *
 * Supported URL formats:
 * - Full domain: https://cms.intellifirenwt.com
 * - Path on same domain: /cms
 * - Localhost with port: localhost:8080
 *
 * Examples:
 * - cms:// -> VITE_CROSS_APP_CMS_URL
 * - admin:// -> VITE_CROSS_APP_ADMIN_URL
 * - dutylog:// -> VITE_CROSS_APP_DUTYLOG_URL
 */

import { getConfig } from '../services/configService';

/**
 * Get cross-app base URL from runtime config
 */
function getCrossAppBaseUrl(token: string): string | null {
  // Convert token to uppercase and replace dashes with underscores for config key lookup
  const tokenUpper = token.toUpperCase().replace(/-/g, '_');
  const configKey = `CROSS_APP_${tokenUpper}_URL` as keyof ReturnType<typeof getConfig>;

  try {
    const config = getConfig();
    const url = config[configKey] as string | undefined;

    if (!url) {
      console.warn(`[CrossAppUrlResolver] No config value found for ${configKey}`);
      return null;
    }

    return url;
  } catch (e) {
    console.warn(`[CrossAppUrlResolver] Config not loaded yet`);
    return null;
  }
}

/**
 * Build full URL from base URL and path
 */
function buildUrl(baseUrl: string, path: string): string {
  // Handle relative paths (e.g., "/cms")
  if (baseUrl.startsWith('/')) {
    // Remove trailing slash from base
    baseUrl = baseUrl.replace(/\/$/, '');
    // Ensure path starts with /
    if (path && !path.startsWith('/')) {
      path = '/' + path;
    }
    return baseUrl + (path || '');
  }

  // Handle localhost with port (e.g., "localhost:8080")
  if (baseUrl.startsWith('localhost')) {
    baseUrl = 'http://' + baseUrl;
  }

  // Handle full URLs (e.g., "https://cms.intellifirenwt.com")
  // Remove trailing slash
  baseUrl = baseUrl.replace(/\/$/, '');

  // Ensure path starts with /
  if (path && !path.startsWith('/')) {
    path = '/' + path;
  }

  return baseUrl + (path || '');
}

/**
 * Resolve a tokenized URL to a full URL
 *
 * @param tokenizedUrl - URL with token prefix (e.g., "cms://admin/users" or "cms://")
 * @returns Full resolved URL or null if token not configured
 *
 * @example
 * // With VITE_CROSS_APP_CMS_URL=https://cms.intellifirenwt.com
 * resolveCrossAppUrl("cms://admin/users") // "https://cms.intellifirenwt.com/admin/users"
 * resolveCrossAppUrl("cms://") // "https://cms.intellifirenwt.com"
 *
 * @example
 * // With VITE_CROSS_APP_CMS_URL=/cms
 * resolveCrossAppUrl("cms://admin") // "/cms/admin"
 *
 * @example
 * // With VITE_CROSS_APP_ADMIN_URL=localhost:8080
 * resolveCrossAppUrl("admin://dashboard") // "http://localhost:8080/dashboard"
 */
export function resolveCrossAppUrl(tokenizedUrl: string): string | null {
  // Check if URL uses token format (single-word token://)
  // Note: This regex explicitly excludes URLs like http:// or https:// by requiring
  // the token to be a simple identifier (letters, numbers, dash, underscore only)
  const tokenMatch = tokenizedUrl.match(/^([a-z0-9_-]+):\/\/(.*)$/i);

  if (!tokenMatch) {
    // Not a tokenized URL, return as-is
    return tokenizedUrl;
  }

  const [, token, path] = tokenMatch;

  // Check if this looks like a standard URL protocol (http, https, ftp, etc.)
  // If so, return as-is since it's not a cross-app token
  const standardProtocols = ['http', 'https', 'ftp', 'ftps', 'file', 'mailto', 'tel', 'data'];
  if (standardProtocols.includes(token.toLowerCase())) {
    return tokenizedUrl;
  }

  const baseUrl = getCrossAppBaseUrl(token);

  if (!baseUrl) {
    return null;
  }

  return buildUrl(baseUrl, path);
}

/**
 * Get all configured cross-app tokens
 * Useful for debugging or admin interfaces
 */
export function getConfiguredCrossAppTokens(): string[] {
  const tokens: string[] = [];

  try {
    const config = getConfig();

    // Check for known cross-app URL keys
    if (config.CROSS_APP_CMS_URL) {
      tokens.push('cms');
    }
    // Add other cross-app tokens as they are configured
  } catch (e) {
    console.warn(`[CrossAppUrlResolver] Config not loaded yet`);
  }

  return tokens;
}
